/**
 * Client FX: the client's Convert page (and limit orders, with Advanced
 * options), and the staff settings for it. The client only ever sees their
 * own rate (spread included), never iFAST's.
 */
import axios, { type AxiosInstance } from "axios";
import { getSessionToken } from "@/lib/session";

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

function makeClient(): AxiosInstance {
  const c = axios.create({ baseURL: `${BASE_URL}/api/v1`, timeout: 60_000 });
  c.interceptors.request.use((config) => {
    const token = getSessionToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });
  return c;
}
const client = makeClient();

export interface CfxConversion {
  id: string; at: string; sell: string; buy: string; sellAmount: number; buyAmount: number;
  status: string; done: boolean; failed: boolean; reference: string | null; tradeId: string | null;
}
export interface CfxOverview {
  enabled: boolean; maxTradeGbp?: number;
  /** Advanced options: limit orders. */
  advanced?: boolean;
  currencies?: { code: string; available: number }[];
  conversions?: CfxConversion[];
}
export interface CfxQuote {
  quoteRef: string; sell: string; buy: string; sellAmount: number; buyAmount: number; sellPerBuy: number; seconds: number; expiresAt: string;
}
export interface CfxSettings {
  accountId: string; enabled: boolean; spreadPct: number | null; maxTradeGbp: number | null;
  /** Currency -> the iFAST account (Bank_Account__c id) the client transacts in. */
  accounts: Record<string, string>;
  /** Advanced options: limit orders. */
  advanced: boolean;
  options?: { currency: string; currencyId: string; accounts: { id: string; name: string; balance: number }[] }[];
  updatedBy: string | null; updatedAt: string | null;
}

/** The client's current rate (spread included): sell currency per 1 buy currency. */
export interface CfxRate { sell: string; buy: string; sellPerBuy: number; seconds: number; at: string }
/** A client's limit order, as they see it: their side only. */
export interface CfxLimitOrder {
  id: string; ref: string; at: string; sell: string; buy: string; sellAmount: number; buyAmount: number;
  rate: number; rateOrientation: "sellPerBuy" | "buyPerSell"; expiresAt: string; status: string;
  open: boolean; done: boolean; failed: boolean; reference: string | null; tradeId: string | null; endedAt: string | null;
}
export interface CfxLimitBody {
  token: string; sell: string; buy: string; side: "buy" | "sell"; amount: number;
  targetRate: number; rateOrientation: "sellPerBuy" | "buyPerSell"; expiresAt: string;
}
/** Staff: per iFAST currency, the client's limit account (or none yet). */
export interface CfxLimitAccount { currency: string; currencyId: string; limitAccount: string | null }

export function cfxErr(e: any, fallback = "Something went wrong"): string {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : e?.message || fallback;
}

async function confirmationBlob(tradeId: string): Promise<{ blob: Blob; filename: string }> {
  const resp = await client.get(`/trades/${tradeId}/confirmation`, { responseType: "blob" });
  const cd = resp.headers["content-disposition"] || "";
  const m = cd.match(/filename="?([^"]+)"?/);
  return { blob: new Blob([resp.data], { type: "application/pdf" }), filename: m ? m[1] : `Trade Confirmation - ${tradeId}.pdf` };
}

export const clientFxApi = {
  /** The trade confirmation PDF, for the preview modal. */
  confirmation: (tradeId: string) => confirmationBlob(tradeId),
  overview: (accountId: string) => client.get<CfxOverview>("/client-fx", { params: { accountId } }).then((r) => r.data),
  sendCode: (accountId: string) => client.post("/client-fx/code", null, { params: { accountId } }).then((r) => r.data),
  verify: (accountId: string, code: string) =>
    client.post<{ token: string; expiresAt: string }>("/client-fx/verify", { code }, { params: { accountId } }).then((r) => r.data),
  quote: (accountId: string, body: { token: string; sell: string; buy: string; side: "buy" | "sell"; amount: number }) =>
    client.post<CfxQuote>("/client-fx/quote", body, { params: { accountId } }).then((r) => r.data),
  convert: (accountId: string, token: string, quoteRef: string) =>
    client.post<{ id: string; sell: string; buy: string; sellAmount: number; buyAmount: number; status: string;
      tradeId: string | null; reference: string | null }>(
      "/client-fx/convert", { token, quoteRef }, { params: { accountId }, timeout: 120_000 }).then((r) => r.data),
  settings: (accountId: string) => client.get<CfxSettings>(`/accounts/${accountId}/client-fx`).then((r) => r.data),
  saveSettings: (accountId: string, body: { enabled: boolean; spreadPct: number | null; maxTradeGbp: number | null; accounts: Record<string, string>; advanced: boolean }) =>
    client.put<CfxSettings>(`/accounts/${accountId}/client-fx`, body).then((r) => r.data),
  limitAccounts: (accountId: string) =>
    client.get<{ currencies: CfxLimitAccount[] }>(`/accounts/${accountId}/client-fx/limit-accounts`).then((r) => r.data.currencies),
  createLimitAccount: (accountId: string, currencyId: string) =>
    client.post<{ currencies: CfxLimitAccount[] }>(`/accounts/${accountId}/client-fx/limit-accounts`, { currencyId }).then((r) => r.data.currencies),
  rate: (accountId: string, p: { sell: string; buy: string; side: "buy" | "sell"; amount: number }) =>
    client.get<CfxRate>("/client-fx/rate", { params: { accountId, ...p } }).then((r) => r.data),
  limitOrders: (accountId: string) =>
    client.get<{ orders: CfxLimitOrder[] }>("/client-fx/limit-orders", { params: { accountId } }).then((r) => r.data.orders),
  placeLimit: (accountId: string, body: CfxLimitBody) =>
    client.post<CfxLimitOrder>("/client-fx/limit-orders", body, { params: { accountId }, timeout: 120_000 }).then((r) => r.data),
  cancelLimit: (accountId: string, id: string, token: string) =>
    client.post<CfxLimitOrder>(`/client-fx/limit-orders/${id}/cancel`, { token }, { params: { accountId }, timeout: 120_000 }).then((r) => r.data),
};
